import { format, isBefore } from 'date-fns'
import { FulfillmentType } from 'shop/types'
import { parseTimezoneISO } from 'shop/utils'
import { useQueryParams } from './useQueryParams'

export const useQueryParamsFulfillment = (
  searchQueryParamsProp?: URLSearchParams
) => {
  const { getQueryParam, hasQueryParam, deleteQueryParam } = useQueryParams(
    searchQueryParamsProp
  )

  const getFulfillmentTypeParam = (): FulfillmentType | undefined => {
    if (!hasQueryParam('fulfillment_type')) return undefined

    return getQueryParam('fulfillment_type')?.toUpperCase() as FulfillmentType
  }

  const getDateQueryParam = (): Date | undefined => {
    if (!hasQueryParam('date')) return undefined
    let dateParam
    try {
      dateParam = new Date(getQueryParam('date'))
    } catch (error) {
      console.warn('Failed to retrieve "date" query param: ', error)
      deleteQueryParam('date')
      return undefined
    }

    // this will also scrub the date query param if it's "Today"
    const now = new Date()
    if (isBefore(dateParam, now)) {
      console.warn('Date query param is in the past')
      deleteQueryParam('date')
      return undefined
    }
    return dateParam
  }

  const getTimeQueryParam = (): string | undefined => {
    if (!hasQueryParam('time')) return undefined
    let timeParam
    try {
      timeParam = decodeURIComponent(getQueryParam('time'))
    } catch (error) {
      console.warn('Failed to retrieve "time" query param: ', error)
      deleteQueryParam('time')
      return undefined
    }
    return timeParam
  }

  const constructDatetimeFromQueryParams = (): Date | undefined => {
    const hasDateParam = hasQueryParam('date')
    const hasTimeParam = hasQueryParam('time')

    if (!hasDateParam && !hasTimeParam) return undefined

    // default date and time params to "now"
    const dateParam = format(getDateQueryParam() || new Date(), 'yyyy-MM-dd')
    const timeParam = getTimeQueryParam() || format(new Date(), 'HH:mm:ss')

    let isoDateTime
    try {
      const construcedDatetime = `${dateParam} ${timeParam}` // e.g. 2025-05-02 16:30
      const localTimezoneDatetime = new Date(construcedDatetime) // e.g. Fri May 02 2025 16:30:00 GMT+0100 (British Summer Time)
      isoDateTime = parseTimezoneISO(localTimezoneDatetime.toISOString()) // e.g. 2025-05-02T15:30:00.000Z
    } catch (error) {
      console.warn('Failed to construct datetime from query params: ', error)
      deleteQueryParam(['date', 'time'])
      return undefined
    }
    return isoDateTime
  }

  const fulfillmentQueryParams = {
    fulfillmentType: getFulfillmentTypeParam(),
    fulfillmentDate: getDateQueryParam(),
    fulfillmentTime: getTimeQueryParam(),
    fulfillmentDatetime: constructDatetimeFromQueryParams()
  }

  return {
    fulfillmentQueryParams
  }
}
